//ICS-215A
import React from 'react';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FemaSectionShell from 'components/IAP/femaCards/FemaSectionShell';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useIncidentAreas } from 'components/IAP/hooks/incidentAreaHooks';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from './OperationalPeriodScheduleControl';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

import { getIncidentCardTitle } from '../USCGPage';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from './icsBoxPrimitives';
import Ics215ASignatureBox from './Ics215ASignatureBox';
import IncidentAreasTable from './IncidentAreasTable';
import SafetyAnalysisVersionRecording from './safetyAnalysisVersionRecording';
import {
  ICS215A_BOX_LABELS,
  ICS215A_DATE_TIME_PREPARED_HINT,
  ICS215A_INCIDENT_AREAS_HINT,
  ICS215A_INCIDENT_LOCATION_HINT,
  ICS215A_INCIDENT_NAME_HINT,
  ICS215A_OPERATIONAL_PERIOD_HINT,
} from './ics215aBoxLabels';

const ICS_215A_EXPORT_FORMNAME = 'ICS_215A_CG';

/**
 * Incident Action Plan Safety Analysis, the ICS 215A-CG.
 *
 * Boxes 1-4 are the read-only header the printed form repeats; boxes 5-8 are
 * the columns of the incident-area table; box 9 is the single Prepared by.
 */
const IAPSafetyAnalysis: React.FC = () => {
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const formFamily = useIcsFormFamily();
  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const readOnly = !canEditCards || readOnlyForms;

  const incidentId = useActualIncidentId();
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { data: incidentAreas } = useIncidentAreas(incidentId);

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: ICS_215A_EXPORT_FORMNAME,
      data: {},
      mapSketch: null,
      format,
    });
  };

  const exportActions = (
    <FormExportButton
      formname={ICS_215A_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-215A"
      disabled={isExporting}
    />
  );

  const areaEditor = (
    <IncidentAreasTable
      incidentId={incidentId}
      areas={incidentAreas ?? []}
      readOnly={readOnly}
    />
  );

  const headerBoxes = (
    <div className="tw:grid tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">
      <Ics204CardSection
        boxLabel={ICS215A_BOX_LABELS.incidentName}
        testId="ics215a-section-incident-name"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the incident name">
            {ICS215A_INCIDENT_NAME_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={incident.data?.name ?? ''}
          testId="ics215a-field-incident-name"
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS215A_BOX_LABELS.incidentLocation}
        testId="ics215a-section-incident-location"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the incident location">
            {ICS215A_INCIDENT_LOCATION_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value={incidentInfo?.incidentLocation || incident.data?.address || ''}
          testId="ics215a-field-incident-location"
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS215A_BOX_LABELS.dateTimePrepared}
        testId="ics215a-section-date-time-prepared"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the date and time prepared">
            {ICS215A_DATE_TIME_PREPARED_HINT}
          </BoxInfo>
        }
      >
        <PrintedValue
          value="Stamped on export"
          testId="ics215a-field-date-time-prepared"
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS215A_BOX_LABELS.operationalPeriod}
        testId="ics215a-section-operational-period"
        className="tw:mt-0 tw:h-full tw:min-w-0"
        headerAction={
          <BoxInfo label="About the operational period">
            {ICS215A_OPERATIONAL_PERIOD_HINT}
          </BoxInfo>
        }
      >
        <OperationalPeriodScheduleControl testId="ics215a-field-operational-period" />
      </Ics204CardSection>
    </div>
  );

  // The FEMA shell renders its own versioning banner and prepared-by section,
  // so only the Coast Guard path adds them here.
  const body =
    formFamily === 'fema' ? (
      <FemaSectionShell baseId="ICS_215A" slots={{ hazardRows: areaEditor }} />
    ) : (
      <div className="tw:flex tw:flex-col tw:gap-3">
        <FormVersioningBanner baseId="ICS_215A" />
        {headerBoxes}
        <Ics204CardSection
          boxLabel={ICS215A_BOX_LABELS.incidentAreas}
          testId="ics215a-section-incident-areas"
          className="tw:mt-0"
          headerAction={
            <BoxInfo label="About incident areas">
              {ICS215A_INCIDENT_AREAS_HINT}
            </BoxInfo>
          }
        >
          {areaEditor}
        </Ics204CardSection>
        <Ics215ASignatureBox periodNumber={effectivePeriodNumber} />
      </div>
    );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return (
      <SafetyAnalysisVersionRecording>
        <div
          className="dark content tw:text-foreground mt-2"
          data-tour="ics215a-card"
        >
          {body}
        </div>
      </SafetyAnalysisVersionRecording>
    );
  }

  return (
    <SafetyAnalysisVersionRecording>
      <Card
        data-testid="uscg-215a-card"
        data-tour="ics215a-card"
        data-form-family="uscg"
        className="dark tw:bg-background tw:text-foreground"
      >
        <CardHeader>
          <CardTitle>{getIncidentCardTitle('iapSafetyAnalysis')}</CardTitle>
          <CardAction>{exportActions}</CardAction>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    </SafetyAnalysisVersionRecording>
  );
};

export default IAPSafetyAnalysis;
